import { HttpError } from '@pressreader/types';

import { publishErrorEvent } from './logger.events';

function isError(value: unknown): value is Error {
    return value instanceof Error;
}

function stringifyError(error: Error): string {
    return `${error.message}.\nStack: ${error.stack}`;
}

function stringifyObject(obj: object): string {
    return JSON.stringify(obj, null, 4);
}

function stringify(anyValueOrError: unknown): string {
    if (anyValueOrError == null) {
        return '';
    }
    if (isError(anyValueOrError)) {
        return stringifyError(anyValueOrError);
    }
    if (typeof anyValueOrError === 'object') {
        return stringifyObject(anyValueOrError);
    }
    return String(anyValueOrError);
}

function getErrorObjectFromParameters(err: unknown): Error {
    return isError(err) ? err : new Error(`Error. ${stringify(err) ?? 'Unknown error'}`);
}

/** The HTTP error itself, or the one it replaced and kept as `cause`. One level deep — the depth
 * every producer in this repo creates. */
function findHttpError(err: unknown): HttpError | undefined {
    if (err instanceof HttpError) {
        return err;
    }

    const cause = isError(err) ? (err as { cause?: unknown }).cause : undefined;
    return cause instanceof HttpError ? cause : undefined;
}

/** Error message for a log record, prefixed with the HTTP status when the error carries one. */
export function formatErrorForLog(err: unknown): string {
    const message = isError(err) ? err.message : String(err);
    const httpError = findHttpError(err);
    if (!httpError) {
        return message;
    }

    const status = `[http=${httpError.status}]`;
    return message ? `${status} ${message}` : status;
}

// Just a reference for now.
export function error(err: unknown, ...otherDetails: Array<unknown>) {
    self.console.error(err, ...otherDetails);

    const errorObj: Error = getErrorObjectFromParameters(err);
    const additionalInfoStringified: string = otherDetails.map(stringify).join('\n\n');

    publishErrorEvent(errorObj, additionalInfoStringified);
}
